import { Component } from '@theme/component';

/**
 * Bundle Selector Component
 *
 * A disclosure-based dropdown for selecting bundle products on PDP.
 * Handles dropdown open/close interactions and navigates to selected bundle's URL.
 *
 * @class BundleSelector
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The dropdown trigger button
 * @property {HTMLElement} disclosureContent – The dropdown content container
 *
 * @extends Component<Refs>
 */
class BundleSelector extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
    this.bindEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEvents();
  }

  /**
   * Bind click events for dropdown and bundle links
   */
  bindEvents() {
    // Close dropdown when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.handleClickOutside);

    // Handle keyboard navigation
    this.handleKeydown = this.handleKeydown.bind(this);
    this.addEventListener('keydown', this.handleKeydown);

    // Handle bundle link clicks for immediate navigation
    this.handleBundleClick = this.handleBundleClick.bind(this);
    const bundleLinks = this.querySelectorAll('.bundle-selector__link');
    bundleLinks.forEach((link) => {
      link.addEventListener('click', this.handleBundleClick);
    });
  }

  /**
   * Remove event listeners on disconnect
   */
  removeEvents() {
    document.removeEventListener('click', this.handleClickOutside);
    this.removeEventListener('keydown', this.handleKeydown);

    const bundleLinks = this.querySelectorAll('.bundle-selector__link');
    bundleLinks.forEach((link) => {
      link.removeEventListener('click', this.handleBundleClick);
    });
  }

  /**
   * Toggle the dropdown open/closed state
   * @param {Event} [event] - Optional event that triggered the toggle
   */
  toggleDisclosure = (event) => {
    // Prevent the click from bubbling to the document click handler
    if (event) {
      event.stopPropagation();
    }

    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');

    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;

    // Focus first item when opening
    if (!expanded) {
      const firstLink = content.querySelector('.bundle-selector__link');
      if (firstLink) {
        requestAnimationFrame(() => {
          firstLink.focus();
        });
      }
    }
  };

  /**
   * Close the dropdown
   */
  closeDisclosure() {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-label', trigger.dataset.disclosureOpen);
    content.inert = true;
  }

  /**
   * Check if dropdown is currently open
   * @returns {boolean}
   */
  isOpen() {
    return this.refs.disclosureTrigger.matches('[aria-expanded="true"]');
  }

  /**
   * Handle clicks outside the dropdown to close it
   * @param {MouseEvent} event
   */
  handleClickOutside(event) {
    if (this.isOpen() && !this.contains(event.target)) {
      this.closeDisclosure();
    }
  }

  /**
   * Handle keyboard navigation within the dropdown
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const { key } = event;

    switch (key) {
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault();
          this.closeDisclosure();
          this.refs.disclosureTrigger.focus();
        }
        break;
      case 'ArrowDown':
        if (this.isOpen()) {
          event.preventDefault();
          this.focusNextItem();
        }
        break;
      case 'ArrowUp':
        if (this.isOpen()) {
          event.preventDefault();
          this.focusPrevItem();
        }
        break;
    }
  }

  /**
   * Focus the next bundle link in the list
   */
  focusNextItem() {
    const links = Array.from(this.querySelectorAll('.bundle-selector__link'));
    const currentIndex = links.findIndex((link) => link === document.activeElement);
    const nextIndex = currentIndex < links.length - 1 ? currentIndex + 1 : 0;
    links[nextIndex]?.focus();
  }

  /**
   * Focus the previous bundle link in the list
   */
  focusPrevItem() {
    const links = Array.from(this.querySelectorAll('.bundle-selector__link'));
    const currentIndex = links.findIndex((link) => link === document.activeElement);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : links.length - 1;
    links[prevIndex]?.focus();
  }

  /**
   * Handle bundle link click - navigate to the bundle's PDP
   * @param {MouseEvent} event
   */
  handleBundleClick(event) {
    const link = event.currentTarget;
    const bundleUrl = link.dataset.bundleUrl || link.href;

    // Check if this is the current product (has aria-current)
    if (link.getAttribute('aria-current') === 'true') {
      event.preventDefault();
      this.closeDisclosure();
      return;
    }

    // Let the default link behavior handle navigation
    // The link already has the href set, so it will navigate naturally
  }
}

if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', BundleSelector);
}
